import { cargarProductoParaDocumento } from './bloqueaArtDiscontinuados';
import productoService from '../services/productoService';
import pedidoVentaService from '../services/pedidoVentaService';
import {
  cantidadHaciaUmStock,
  umParaDocumento,
  umStockDesdeDetalle,
  aplicarUmLogisticaProducto,
  umDesdeEquivalenciasSiCorresponde,
  umBaseProducto,
  factorCantidadEntreUm,
  precioEnUmDocumento,
} from './umVentaDocumento';

const round5 = (n: number) => Math.round(n * 100000) / 100000;

export type RemitoItemUmInput = {
  codigoArticulo: string;
  cantidad: number;
  precioUnitario: number;
  um?: string | null;
  nroFormularioMaster?: number | null;
  nroItemMaster?: number | null;
};

export type ConvertirRemitoOptions = {
  /** Remito generado desde factura (misma conversión que cualquier otro origen). */
  desdeFactura?: boolean;
};

/** Recupera la UM del pedido origen cuando la factura no trae UM. */
async function umDesdePedidoMaster(
  nroFormularioMaster?: number | null,
  nroItemMaster?: number | null,
  codigoArticulo?: string,
): Promise<string> {
  const nfm = Number(nroFormularioMaster);
  if (!Number.isFinite(nfm) || nfm <= 0) return '';
  try {
    const pedido = await pedidoVentaService.getPedidoVenta(nfm);
    const detalles = pedido.detalles || [];
    const nim = Number(nroItemMaster);
    let line =
      Number.isFinite(nim) && nim > 0
        ? detalles.find((d) => Number(d.nroItem) === nim)
        : undefined;
    const cod = String(codigoArticulo ?? '').trim();
    if (!line && cod) {
      line = detalles.find((d) => String(d.codigoArticulo ?? '').trim() === cod);
    }
    return String(line?.um ?? (line as { Um?: string } | undefined)?.Um ?? '').trim();
  } catch {
    return '';
  }
}

/**
 * Si hay varias equivalencias (KG=10 y PAQ=6), elige la UM cuyo precio
 * convertido se acerca más al precio unitario de la línea.
 */
function umPorPrecioMasCercano(
  precioLinea: number,
  precioBase: number,
  umBase: string,
  eqs: { umOrig: string; umDest: string; cantDest: number }[],
): string {
  const base = Number(precioBase) || 0;
  const linea = Number(precioLinea) || 0;
  if (!(base > 0) || !(linea > 0) || !eqs.length) return '';

  let bestUm = '';
  let bestDiff = Number.POSITIVE_INFINITY;
  const candidatos = new Set<string>();
  for (const e of eqs) {
    const o = String(e.umOrig ?? '').trim();
    const d = String(e.umDest ?? '').trim();
    if (o) candidatos.add(o);
    if (d) candidatos.add(d);
  }
  for (const um of Array.from(candidatos)) {
    if (!um || um.toUpperCase() === String(umBase).toUpperCase()) continue;
    const precioUm = precioEnUmDocumento(base, um, umBase, eqs);
    const diff = Math.abs(precioUm - linea);
    const rel = diff / Math.max(linea, base);
    if (rel < bestDiff) {
      bestDiff = rel;
      bestUm = um;
    }
  }
  // Solo aceptar si está razonablemente cerca (tolera redondeos / descuentos chicos)
  return bestDiff <= 0.15 ? bestUm : '';
}

/**
 * Convierte ítem de remito a UM de inventario.
 * Respeta la UM de la línea (pedido→factura→KG, factura directa→PAQ).
 * 1 KG (=10 UNI) → 10; 1 PAQ (=6 UNI) → 6.
 */
export async function convertirItemRemitoAUmStock<T extends RemitoItemUmInput>(
  item: T,
  _options?: ConvertirRemitoOptions,
): Promise<T> {
  const codigo = String(item.codigoArticulo ?? '').trim();
  const cantidadDoc = Number(item.cantidad) || 0;
  const precioDoc = Number(item.precioUnitario) || 0;
  let umDoc = String(item.um ?? '').trim();
  if (!codigo || cantidadDoc <= 0) return item;

  try {
    // Si la factura no trajo UM, recuperar la del pedido master (evita caer en umrefFac/PAQ).
    if (!umDoc) {
      umDoc = await umDesdePedidoMaster(
        item.nroFormularioMaster,
        item.nroItemMaster,
        codigo,
      );
    }

    const detalle = await productoService.getEquivalenciasUmDetalle(codigo);
    const umStock = umStockDesdeDetalle(detalle);
    if (!umStock) return item;

    const productoUm = aplicarUmLogisticaProducto({ codigoArticulo: codigo }, detalle);
    const umBase = umBaseProducto(productoUm) || detalle.umBase || umStock;
    const eqs = detalle.equivalencias ?? [];

    if (!umDoc) {
      // Preferir UM coherente con el precio de la línea antes que umrefFac genérico
      let precioBase = 0;
      try {
        const prod = await cargarProductoParaDocumento(codigo, 'VENTAS');
        precioBase = Number(
          (prod as { precioVenta?: number })?.precioVenta ??
            (prod as { PrecioVenta?: number })?.PrecioVenta ??
            0,
        );
      } catch {
        /* sin catálogo */
      }
      umDoc = umPorPrecioMasCercano(precioDoc, precioBase, umBase, eqs);

      if (!umDoc) {
        const vieneDePedido =
          Number(item.nroFormularioMaster) > 0 &&
          Number(item.nroFormularioMaster) !== Number((item as { nroFormularioFactura?: number }).nroFormularioFactura);
        const ctx = vieneDePedido ? 'pedido' : 'factura';
        const umLogistica = umParaDocumento(productoUm, ctx);
        umDoc =
          (umLogistica && umLogistica.toUpperCase() !== String(umBase).toUpperCase()
            ? umLogistica
            : '') ||
          umDesdeEquivalenciasSiCorresponde('', umBase, eqs) ||
          umStock;
      }
    }

    const factor = factorCantidadEntreUm(umDoc, umStock, eqs, umStock);
    if (factor == null || !Number.isFinite(factor) || Math.abs(factor - 1) < 1e-9) {
      return item;
    }

    const cantidadStock = cantidadHaciaUmStock(cantidadDoc, umDoc, umStock, eqs);
    if (!(cantidadStock > 0)) return item;

    const precioPorUmStock = round5((cantidadDoc * precioDoc) / cantidadStock);

    return {
      ...item,
      cantidad: round5(cantidadStock),
      precioUnitario: precioPorUmStock,
      um: umStock,
    };
  } catch {
    return item;
  }
}

export async function convertirItemsRemitoAUmStock<T extends RemitoItemUmInput>(
  items: T[],
  options?: ConvertirRemitoOptions,
): Promise<T[]> {
  return Promise.all(items.map((it) => convertirItemRemitoAUmStock(it, options)));
}